import { ReactNode, useMemo } from 'react'
import classNames from 'classnames'
import { Venue, VenueEvent } from 'types/model'
import { DayDetail } from 'types/ui/calendar'

import {
  getBusinessDate,
  getCalendarDays,
  isVenueEventPassedByInstant,
  withDateFormat,
} from '@utils/date-time'
import { createLookup } from '@utils/lookup'
import { EventCalendarCell } from './event-calendar-cell'
import { Skeleton } from '@elements/skeleton'

export interface EventCalendarDesktopTableProps {
  stickyHeader?: boolean
  venue: Venue
  events: VenueEvent[]
  month: number
  year: number
  loading?: boolean
  onCellClick: (event: VenueEvent) => void
}

export function EventCalendarDesktopTable({
  stickyHeader = true,
  venue,
  events,
  month,
  year,
  loading,
  onCellClick,
}: EventCalendarDesktopTableProps) {
  const nowMs = Date.now()
  const businessStartMs = getBusinessDate().valueOf()

  const calendarRows = useMemo<DayDetail[][]>(() => {
    const calendarDays = getCalendarDays(year, month)
    const rows: DayDetail[][] = []

    for (let i = 7; i <= calendarDays.length; i += 7) {
      rows.push(calendarDays.slice(i - 7, i))
    }
    return rows
  }, [month, year])

  const eventsByCalendarDay = useMemo(
    () =>
      createLookup({
        of: events.map((event) => {
          const dayKey = getCalendarDayKeyFromEvent(event)
          return {
            ...event,
            dateId: dayKey,
          }
        }),
        by: 'dateId',
      }),
    [events]
  )

  return (
    <div className="hidden w-full md:block">
      <table className="relative w-full border-separate rounded-lg rounded-t-lg border-spacing-0 !border-none !border-transparent !border-t-transparent !border-t-none !bg-transparent">
        <thead>
          <tr
            className={classNames(
              'rounded-t-lg !border-none !bg-transparent !border-transparent z-[1]',
              {
                'sticky top-[96px] md:top-[64px]': stickyHeader,
              }
            )}
          >
            <EventCalendarHeaderCell>Sun</EventCalendarHeaderCell>
            <EventCalendarHeaderCell>Mon</EventCalendarHeaderCell>
            <EventCalendarHeaderCell>Tue</EventCalendarHeaderCell>
            <EventCalendarHeaderCell>Wed</EventCalendarHeaderCell>
            <EventCalendarHeaderCell>Thu</EventCalendarHeaderCell>
            <EventCalendarHeaderCell>Fri</EventCalendarHeaderCell>
            <EventCalendarHeaderCell>Sat</EventCalendarHeaderCell>
          </tr>
        </thead>
        <tbody className="w-full border-2 border-standard-light">
          {calendarRows.map((row, rowIdx) => {
            return (
              <tr
                key={`${year}-${month}-${rowIdx}`}
                className={classNames('group/row !bg-transparent', {
                  'last-row': rowIdx === calendarRows.length - 1,
                })}
              >
                {row.map((dayDetail: DayDetail) => {
                  const dayKey = formatDayKey(
                    dayDetail.year,
                    dayDetail.month + 1,
                    dayDetail.day
                  )
                  const event = eventsByCalendarDay[dayKey]
                  return (
                    <td
                      key={dayDetail.rfc}
                      className="relative w-[14.28%] !p-0 !bg-transparent border-b-2 group-[.last-row]:first-of-type:rounded-bl-lg group-[.last-row]:last-of-type:rounded-br-lg border-r-2 !border-foreground/[.2] dark:!border-dark-foreground/[.15] first-of-type:border-l-2"
                    >
                      {loading && <Skeleton className="w-full aspect-square" />}
                      {!loading && dayDetail.month === month && (
                        <EventCalendarCell
                          onClick={onCellClick}
                          venue={venue}
                          event={event}
                          dayDetail={dayDetail}
                          pastEvent={
                            !!event
                              ? isVenueEventPassedByInstant(
                                  nowMs,
                                  event.dateStart,
                                  event.dateEnd,
                                  {
                                    timezone:
                                      event.timezone || venue.location.timezone,
                                  }
                                )
                              : dayDetail.timestamp < businessStartMs
                          }
                        />
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function getCalendarDayKeyFromEvent(event: VenueEvent): string {
  const diaryDate = new Date(event.date)
  if (!Number.isNaN(diaryDate.valueOf())) {
    return formatDayKey(
      diaryDate.getUTCFullYear(),
      diaryDate.getUTCMonth() + 1,
      diaryDate.getUTCDate()
    )
  }

  const eventTimezone = event.timezone || 'UTC'
  const timezoneKey = withDateFormat(event.dateStart, 'yyyy-MM-dd', eventTimezone)
  if (timezoneKey) return timezoneKey

  const fallbackDate = new Date(event.dateStart)
  return formatDayKey(
    fallbackDate.getUTCFullYear(),
    fallbackDate.getUTCMonth() + 1,
    fallbackDate.getUTCDate()
  )
}

function formatDayKey(year: number, month: number, day: number): string {
  return `${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function EventCalendarHeaderCell({ children }: { children: ReactNode }) {
  return (
    <th className="!text-center !border-none !border-transparent bg-primary-600 dark:bg-primary-600 py-2 heading-md !text-dark-foreground w-[14.28%] first-of-type:rounded-tl-lg last-of-type:rounded-tr-lg">
      {children}
    </th>
  )
}
